import { useMutation, useQuery } from '@apollo/client/react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Code,
  HStack,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import {
  DataTypeConfigUtils,
  ManualContentHelpers,
  RuntimeOperations,
} from '@helo/cms'
import { List, flow, pipe } from '@helo/utils'
import { useMemo, useRef, useState } from 'react'

import { showToast } from '../../components/Toast/showToast'
import type { ApiKey } from '../../services/apiKeyService'
import {
  apiKeysQuery,
  revokeApiKeyMutation,
} from '../../services/apiKeyService'
import { useAppStore } from '../../store/appStore/appStore'
import { CreateApiKeyModal } from './CreateApiKeyModal'

export const ApiKeysEditor = () => {
  const [isCreateKeyModalOpen, setIsCreateKeyModalOpen] = useState(false)
  const [keyToRevoke, setKeyToRevoke] = useState<string | null>(null)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const apiKeyType = useAppStore(state =>
    pipe(
      state.cms.baseDataTypeConfigs,
      DataTypeConfigUtils.find([['typeName', 'ApiKeyType']]),
    ),
  )

  if (!apiKeyType) {
    throw new Error('ApiKeyType not found')
  }

  const apiKeysListVariables = useMemo(
    () => ({
      first: 200,
      skip: 0,
      orderBy: [{ createdDate: 'DESC' }] as const,
    }),
    [],
  )

  const { data, loading: isLoading } = useQuery(apiKeysQuery, {
    variables: apiKeysListVariables,
  })
  const apiKeys = ((data as { ApiKey_list?: { items?: ApiKey[] } })?.ApiKey_list
    ?.items ?? []) as ApiKey[]
  const apiKeysWithoutInactive = apiKeys.filter(key => key.isActive)

  const apiKeysAsContentNodes = useMemo(
    () =>
      pipe(
        apiKeysWithoutInactive,
        List.map(
          flow(
            (view: ApiKey) =>
              ManualContentHelpers.createContentNode(apiKeyType, view),
            RuntimeOperations.processContentNode(),
          ),
        ),
      ),
    [apiKeysWithoutInactive],
  )

  const [revokeKeyMutation] = useMutation(revokeApiKeyMutation, {
    refetchQueries: [{ query: apiKeysQuery, variables: apiKeysListVariables }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      showToast({
        id: `api-key-revoked`,
        title: 'API Key Revoked',
        description: `Your API key has been revoked.`,
      })
    },
    onError: () => {
      showToast({
        id: `api-key-revoke-error`,
        title: 'Error Revoking API Key',
        description: `There was an error revoking your API key.`,
        variant: 'error',
      })
    },
  })

  const handleCreateKey = () => {
    setIsCreateKeyModalOpen(true)
  }

  const handleRevokeKey = (id: string) => {
    setKeyToRevoke(id)
    onOpen()
  }

  const confirmRevoke = () => {
    if (keyToRevoke) {
      void revokeKeyMutation({ variables: { id: keyToRevoke } })
    }
    onClose()
    setKeyToRevoke(null)
  }

  return (
    <VStack align="stretch">
      <HStack justify="space-between">
        <Text fontSize="12px">
          Add to <Code>x-api-key</Code> HTTP header
        </Text>
        <Button onClick={handleCreateKey}>Create Key</Button>
      </HStack>
      <CreateApiKeyModal
        isOpen={isCreateKeyModalOpen}
        onClose={() => setIsCreateKeyModalOpen(false)}
      />

      {/* Header Row */}
      <HStack
        bg="gray.50"
        p={3}
        borderBottom="1px solid"
        borderColor="gray.200"
        fontWeight="bold"
        fontSize="sm"
      >
        <Box flex={1}>Name</Box>
        <Box flex={1}>Created At</Box>
        <Box flex={1}>Expires At</Box>
        <Box flex={1}>Last Used At</Box>
        <Box flex={1}>Actions</Box>
      </HStack>

      {/* Data Rows */}
      <VStack align="stretch" spacing={0} maxH="400px" overflowY="auto">
        {isLoading ? (
          <Box p={4} textAlign="center">
            <Text>Loading...</Text>
          </Box>
        ) : (
          apiKeysAsContentNodes.map((apiKey, index) => {
            const keyRow = apiKey.data as ApiKey
            return (
              <HStack
                key={keyRow.id || index}
                p={3}
                borderBottom="1px solid"
                borderColor="gray.100"
                _hover={{ bg: 'gray.50' }}
                fontSize="sm"
              >
                <Box flex={1}>{keyRow.name}</Box>
                <Box flex={1}>{keyRow.createdDate}</Box>
                <Box flex={1}>{keyRow.expiresAt}</Box>
                <Box flex={1}>{keyRow.lastUsedAt}</Box>
                <Box flex={1}>
                  <Button
                    variant="dangerButton"
                    size="sm"
                    onClick={() => handleRevokeKey(keyRow.id)}
                  >
                    Revoke
                  </Button>
                </Box>
              </HStack>
            )
          })
        )}
      </VStack>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Revoke API Key
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to revoke this API key? This action cannot
              be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button variant="dangerButton" onClick={confirmRevoke} ml={3}>
                Revoke
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  )
}
